import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { RiskBanner } from "@/components/shared/RiskBanner";
import { Icon } from "@/lib/icons";
import { localizeDeep } from "@/lib/localize";

const defaultValues = [
  { title: "Transparency", description: "We publish real performance reports and keep our claims honest.", icon: "TrendingUp" },
  { title: "Education-first", description: "Our focus is developing skilled, independent traders.", icon: "Target" },
  { title: "Integrity", description: "Verified benefits and secure handling of member data.", icon: "ShieldCheck" },
  { title: "Community", description: "A supportive ecosystem where traders grow together.", icon: "Users" },
];

export default function About() {
  const { t, lang } = useLang();
  useSEO("About INVIXA", "About INVIXA TRADERS — a premium trading ecosystem.");
  const [rawC, setRawC] = useState({});
  useEffect(() => { api.get("/content/about").then((r) => setRawC(r.data.value || {})).catch(() => {}); }, []);
  const c = localizeDeep(rawC, lang);

  const introTitle = c.intro?.title || c.title || "About INVIXA TRADERS";
  const introDesc = c.intro?.description || c.body || "INVIXA is a premium trading ecosystem focused on trading education, market insights, VIP membership, broker partnerships, performance transparency, community, and trader development. We connect these pillars into one professional platform — while trading signals remain exclusively within our private Telegram VIP channel.";
  const values = (c.values && c.values.length ? c.values.filter((v) => v.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)) : defaultValues);

  return (
    <div>
      <section className="radial-cyan">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <span className="font-mono text-xs uppercase tracking-widest text-brand-cyan">{t("nav_about")}</span>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold text-white">{introTitle}</h1>
          <p className="mt-6 text-lg text-slate-400 leading-relaxed">{introDesc}</p>
        </div>
      </section>

      {(c.who?.heading || c.who?.body || c.what?.heading || c.what?.body) && (
        <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-2 gap-6">
          {(c.who?.heading || c.who?.body) && (
            <div className="card-surface rounded-2xl p-7"><h2 className="font-display text-xl font-bold text-white">{c.who?.heading || "Who We Are"}</h2><p className="mt-3 text-slate-400 leading-relaxed whitespace-pre-wrap">{c.who?.body}</p></div>
          )}
          {(c.what?.heading || c.what?.body) && (
            <div className="card-surface rounded-2xl p-7"><h2 className="font-display text-xl font-bold text-white">{c.what?.heading || "What We Do"}</h2><p className="mt-3 text-slate-400 leading-relaxed whitespace-pre-wrap">{c.what?.body}</p></div>
          )}
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((val, i) => (
            <div key={val.id || i} className="card-surface rounded-2xl p-7">
              <div className="h-11 w-11 rounded-xl gradient-brand flex items-center justify-center mb-4"><Icon name={val.icon || "ShieldCheck"} className="h-5 w-5 text-brand-navy" /></div>
              <h3 className="font-display font-semibold text-white">{val.title}</h3>
              <p className="mt-2 text-sm text-slate-400">{val.description}</p>
            </div>
          ))}
        </div>

        {c.final_cta?.heading && (
          <div className="mt-10 rounded-3xl gradient-brand p-[1px]">
            <div className="rounded-3xl bg-brand-navy2 px-8 py-12 text-center">
              <h2 className="font-display text-2xl font-bold text-white">{c.final_cta.heading}</h2>
              {c.final_cta.description && <p className="mt-3 text-slate-400 max-w-xl mx-auto">{c.final_cta.description}</p>}
              {c.final_cta.cta_text && <a href={c.final_cta.cta_link || "/signup"} className="mt-6 inline-block rounded-full gradient-brand text-brand-navy font-semibold px-8 py-3">{c.final_cta.cta_text}</a>}
            </div>
          </div>
        )}

        <div className="mt-10 card-surface rounded-2xl p-6 text-center text-sm text-slate-500">
          Company registration, address & regulatory details — <span className="text-slate-400">[PLACEHOLDER — to be provided by INVIXA]</span>
        </div>
      </section>
      <RiskBanner />
    </div>
  );
}
